import { useEffect, useRef } from 'react';

import { registerDirtyCard } from '@/features/patient-record/lib/dirtyRegistry';
import { useDirtyScope } from '@/features/patient-record/lib/dirtyScope';

interface DirtyRegistrationOptions {
  /** Register only while true — i.e. the form is open AND has unsaved input. */
  readonly active: boolean;
  /** Human-readable name of the form, shown in the unsaved-changes prompt. */
  readonly label: string;
  /** Commit the draft (the form's own submit). Must be safe to call when already submitting. */
  readonly save: () => void;
  /** Throw the draft away (close/reset the form). */
  readonly discard: () => void;
  /** Translated NAMES (no values) of the fields with input, for the prompt. */
  readonly changedFields?: () => readonly string[];
  /** Is the user typing in this form right now? Used by the idle flush to leave it alone. */
  readonly isEditing?: () => boolean;
  /** Can the draft be committed now? False when required fields are still empty (PTV2-1301). */
  readonly canSave?: () => boolean;
}

/**
 * PTV2-1301 — register an add/create form (dialog or inline) in the shared dirty registry while it
 * holds unsaved input, so the section-navigation blocker and the pedigree node-switch flush both see
 * it. Generalises the {@link DirtyFooter} card registration to forms that are not backed by a saved
 * record yet.
 *
 * Callbacks are held in refs so a re-render can't leave a stale closure registered; the effect only
 * re-subscribes when `active`, `scopeId` or `label` change. `scopeId` comes from
 * {@link DirtyScopeContext} — `null` on the patient record, the person guid in the pedigree node
 * editor.
 */
export function useDirtyRegistration({
  active,
  label,
  save,
  discard,
  changedFields,
  isEditing,
  canSave,
}: DirtyRegistrationOptions): void {
  const scopeId = useDirtyScope();
  const saveRef = useRef(save);
  saveRef.current = save;
  const discardRef = useRef(discard);
  discardRef.current = discard;
  const changedRef = useRef(changedFields);
  changedRef.current = changedFields;
  const editingRef = useRef(isEditing);
  editingRef.current = isEditing;
  const canSaveRef = useRef(canSave);
  canSaveRef.current = canSave;

  useEffect(() => {
    if (!active) return undefined;
    return registerDirtyCard({
      scopeId,
      label,
      save: () => { saveRef.current(); },
      discard: () => { discardRef.current(); },
      changedFields: () => changedRef.current?.() ?? [],
      isEditing: () => editingRef.current?.() ?? false,
      canSave: () => canSaveRef.current?.() ?? true,
    });
  }, [active, scopeId, label]);
}
